'use client';

import {
  Children,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Slot } from '@radix-ui/react-slot';
import pluralize from 'pluralize';

import { Button, ButtonSize, ButtonVariant } from '~/ui/components/button';

import styles from './expandableLinks.module.scss';

const ExpandedContext = createContext<{
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}>({
  isExpanded: false,
  setIsExpanded: () => null,
});

export interface ExpandableProviderProps {
  children: React.ReactNode;
  term: string;
  listId: string;
  limit: number;
  total: number;
}

export function ExpandableProvider({
  children,
  term,
  listId,
  limit,
  total,
}: ExpandableProviderProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <ExpandedContext.Provider value={{ isExpanded, setIsExpanded }}>
      {children}
      <Button
        variant={ButtonVariant.SECONDARY}
        size={ButtonSize.REGULAR}
        onClick={() => setIsExpanded(!isExpanded)}
        className={styles.button}
        aria-expanded={isExpanded}
        aria-controls={listId}
      >
        {isExpanded
          ? `Show fewer ${pluralize(term, 2)}`
          : `Show ${total - limit} more ${pluralize(term, total - limit)}`}
      </Button>
    </ExpandedContext.Provider>
  );
}

export interface ExpandableContentProps {
  children: React.ReactNode;
}

export function ExpandableContent({ children }: ExpandableContentProps) {
  return Children.map(children, child => <ExpandableContentItem>{child}</ExpandableContentItem>);
}

function ExpandableContentItem({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const { isExpanded, setIsExpanded } = useContext(ExpandedContext);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    // @ts-expect-error React doesn't support the hidden="until-found" attribute or the beforematch event
    el.hidden = isExpanded ? false : 'onbeforematch' in document.body ? 'until-found' : true;
  }, [isExpanded]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const onBeforeMatch = () => {
      setIsExpanded(true);
    };
    el.addEventListener('beforematch', onBeforeMatch);
    return () => el.removeEventListener('beforematch', onBeforeMatch);
  }, [setIsExpanded]);

  return (
    <Slot ref={ref} className={styles.hiddenItem}>
      {children}
    </Slot>
  );
}
